import React, { createContext, useState, useEffect, ReactNode } from 'react';

const AVIF_DATA_URI =
  'data:image/avif;base64,AAAAHGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZgAAANZtZXRhAAAAAAAAACFoZGxyAAAAAAAAAABwaWN0AAAAAAAAAAAAAAAAAAAAAA5waXRtAAAAAAABAAAAImlsb2MAAAAAREAAAQABAAAAAAD6AAEAAAAAAAAAGgAAACNpaW5mAAAAAAABAAAAFWluZmUCAAAAAAEAAGF2MDEAAAAAVmlwcnAAAAA4aXBjbwAAAAxhdjFDgQAMAAAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAABZpcG1hAAAAAAAAAAEAAQOBAgMAAAAibWRhdBIACggYAAYICGg0IDIMGAAKKKKEAACwEpp4';
const WEBP_DATA_URI = 'data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=';

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = src;
    image.onload = () => resolve(image);
    image.onerror = reject;
  });
}

async function checkIfSupports(uri: string): Promise<boolean> {
  try {
    const image = await loadImage(uri);
    return image.width > 0 && image.height > 0;
  } catch {
    return false;
  }
}

interface ImageSupportContextProps {
  supportsAvif: boolean;
  supportsWebp: boolean;
  isLoading: boolean;
}

const ImageSupportContext = createContext<ImageSupportContextProps>({
  supportsAvif: false,
  supportsWebp: false,
  isLoading: true,
});

interface ImageSupportProviderProps {
  children: ReactNode;
}

export const ImageSupportProvider: React.FC<ImageSupportProviderProps> = ({ children }) => {
  const [supportsAvif, setSupportsAvif] = useState(false);
  const [supportsWebp, setSupportsWebp] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function detectSupport() {
      try {
        const [avif, webp] = await Promise.all([checkIfSupports(AVIF_DATA_URI), checkIfSupports(WEBP_DATA_URI)]);

        if (isMounted) {
          setSupportsAvif(avif);
          setSupportsWebp(webp);
          setIsLoading(false);
        }
        // oxlint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    void detectSupport();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <ImageSupportContext.Provider value={{ supportsAvif, supportsWebp, isLoading }}>
      {children}
    </ImageSupportContext.Provider>
  );
};

export default ImageSupportContext;
